import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Info, Plus, Save, Search, Trash2 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useFormSuccess } from "@/shared/hooks/useFormSuccess";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { fetchAdminEmployeeFamilyMembers, fetchAdminEmployees, lookupAdminEmployee } from "../api/adminApi";
import {
  createAdminProforma,
  createHospitalProforma,
  fetchAdminProformaMeta,
  fetchAuditorProforma,
  fetchHospitalProformaMeta,
  updateAdminProforma,
  type CreateProformaActPayload,
  type ProformaMetaCompany,
  type ProformaMetaInsurancePackage,
  type ProformaMetaHospital,
  type ProformaMetaPackage,
  fetchInsurerRequestEmployees,
  fetchInsurerRequestFamily,
  fetchInsurerRequestHospitalActs,
  lookupInsurerRequestEmployee,
  fetchAdminRequestActs,
} from "../api/proformaApi";
import {
  fetchHospitalCompanyEmployees,
  fetchHospitalEmployeeFamilyMembers,
  fetchHospitalMedicalActs,
  lookupHospitalEmployeeByMatricule,
} from "../api/hospitalApi";
import { getAccountTypeRoute, getAuthToken, getStoredAccountType } from "../auth";

type FamilyMember = {
  id: number;
  name: string;
  relationship: string;
};

type CompanyEmployeeOption = {
  id: number;
  name: string;
  system_id: string;
  company_id: number;
};

/** Acte proposé : id du catalogue, ou null pour un acte de la grille pas encore au catalogue. */
type AvailableAct = {
  key: string;
  id: number | null;
  code: string;
  label: string;
  unitPrice: number;
  source?: string;
};

/** Acte de la demande ; hospital_act_id = 0 pour un acte de la grille ou saisi à la main (créé à l'enregistrement). */
type SelectedActRow = {
  key: string;
  hospital_act_id: number;
  code: string;
  label: string;
  quantity: number;
  unit_price: number;
};

function actKey(id: number | null, code: string): string {
  return id ? `h${id}` : `c${code.toUpperCase()}`;
}

function actUnitPrice(act: { cost?: number; cost_usd?: number }): number {
  return Number(act.cost_usd ?? act.cost ?? 0);
}

export function CreateAuthorizationRequestPage({ editId }: { editId?: number } = {}) {
  const { t } = useTranslation();
  const saveSuccess = useFormSuccess();
  const { showToast } = usePageActions();
  const labels = t.safecheck.requests;
  const formLabels = labels.createForm;

  const [accountType] = useState(() => getStoredAccountType());
  const isAdmin = accountType === "admin";
  // Compte Assurance : l'hôpital envoie la demande à l'assureur, qui la saisit ici.
  const isInsurer = accountType === "enterprise";
  /** Saisie pour un hôpital choisi (Admin ou Assurance). */
  const isDesk = isAdmin || isInsurer;
  const isEditMode = Boolean(editId && editId > 0 && isDesk);
  const canCreate = isDesk;
  const homeHref = accountType ? getAccountTypeRoute(accountType) : "/dashboard/hospital";

  const [hospitals, setHospitals] = useState<ProformaMetaHospital[]>([]);
  const [packages, setPackages] = useState<ProformaMetaPackage[]>([]);
  const [companies, setCompanies] = useState<ProformaMetaCompany[]>([]);
  const [insurers, setInsurers] = useState<Array<{ id: number; name: string }>>([]);
  const [insurancePackages, setInsurancePackages] = useState<ProformaMetaInsurancePackage[]>([]);
  const [insurerId, setInsurerId] = useState("");
  const [insurancePackageId, setInsurancePackageId] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [packageId, setPackageId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [systemId, setSystemId] = useState("");
  const [employeeLabel, setEmployeeLabel] = useState("");
  const [companyEmployees, setCompanyEmployees] = useState<CompanyEmployeeOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [patientType, setPatientType] = useState<"employee" | "dependent">("employee");
  const [selectedDependentId, setSelectedDependentId] = useState("");
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loadingDependents, setLoadingDependents] = useState(false);
  const [availableActs, setAvailableActs] = useState<AvailableAct[]>([]);
  const [selectedActs, setSelectedActs] = useState<SelectedActRow[]>([]);
  const [actSearch, setActSearch] = useState("");
  const [manualAct, setManualAct] = useState({ code: "", label: "", price: "" });
  // Le compte Assurance ne voit pas les codes actes.
  const hideActCodes = accountType === "enterprise";
  const [urgency, setUrgency] = useState("normal");
  const [hospitalNotes, setHospitalNotes] = useState("");
  const [proformaPdf, setProformaPdf] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [loadingActs, setLoadingActs] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);

  const filteredPackages = useMemo(() => {
    if (!isDesk || !hospitalId) return packages;
    const hid = Number(hospitalId);
    return packages.filter((pkg) => !pkg.hospital_id || pkg.hospital_id === hid);
  }, [isDesk, hospitalId, packages]);

  // Cascade du cahier des charges : assureur → package → hôpital → entreprise → patient.
  const insurerPackages = useMemo(
    () => insurancePackages.filter((pkg) => !insurerId || pkg.insurer_id === Number(insurerId)),
    [insurancePackages, insurerId],
  );
  const selectedPackage = insurancePackages.find((pkg) => pkg.id === Number(insurancePackageId)) ?? null;
  const visibleHospitals = useMemo(
    () => (selectedPackage && selectedPackage.hospital_ids.length > 0 ? hospitals.filter((h) => selectedPackage.hospital_ids.includes(h.id)) : hospitals),
    [hospitals, selectedPackage],
  );
  const visibleCompanies = useMemo(
    () =>
      companies.filter(
        (c) =>
          (!insurerId || c.insurer_id === undefined || c.insurer_id === Number(insurerId)) &&
          (!selectedPackage || selectedPackage.company_ids.length === 0 || selectedPackage.company_ids.includes(c.id)),
      ),
    [companies, insurerId, selectedPackage],
  );
  const packageRequired = insurerPackages.length > 0;

  const estimatedTotal = useMemo(
    () =>
      selectedActs.reduce(
        (sum, act) => sum + act.quantity * act.unit_price,
        0,
      ),
    [selectedActs],
  );

  const filteredAvailableActs = useMemo(() => {
    const q = actSearch.trim().toLowerCase();
    const selectedKeys = new Set(selectedActs.map((row) => row.key));
    const base = availableActs.filter((act) => !selectedKeys.has(act.key));
    if (!q) return base;
    return base.filter((act) => {
      const code = (act.code ?? "").toLowerCase();
      const label = (act.label ?? "").toLowerCase();
      return code.includes(q) || label.includes(q);
    });
  }, [actSearch, availableActs, selectedActs]);

  useEffect(() => {
    if (!getAuthToken() || !canCreate) return;

    let cancelled = false;
    void (async () => {
      try {
        if (isDesk) {
          const meta = await fetchAdminProformaMeta();
          if (cancelled) return;
          setHospitals(meta.hospitals ?? []);
          setPackages(meta.packages ?? []);
          setCompanies(meta.companies ?? []);
          setInsurers(meta.insurers ?? []);
          setInsurancePackages(meta.insurance_packages ?? []);
          // Compte Assurance : son assureur est imposé.
          if ((meta.insurers ?? []).length === 1) setInsurerId(String(meta.insurers?.[0]?.id ?? ""));
        } else {
          const meta = await fetchHospitalProformaMeta();
          if (cancelled) return;
          setPackages(meta.packages ?? []);
          setCompanies(meta.companies ?? []);
        }
      } catch {
        /* keep empty selects */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountType, canCreate, isDesk]);

  useEffect(() => {
    if (!isDesk || !hospitalId || !getAuthToken()) return;

    let cancelled = false;
    void (async () => {
      try {
        const meta = await fetchAdminProformaMeta(Number(hospitalId));
        if (cancelled) return;
        setPackages(meta.packages ?? []);
        setPackageId("");
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hospitalId, isDesk]);

  useEffect(() => {
    if (!getAuthToken() || !canCreate) return;
    if (isDesk && !hospitalId) {
      setAvailableActs([]);
      if (!isEditMode) {
        setSelectedActs([]);
      }
      return;
    }

    let cancelled = false;
    setLoadingActs(true);
    void (async () => {
      try {
        if (isDesk) {
          // Grille tarifaire en vigueur de l'assureur (prix négociés), puis catalogue du prestataire.
          const result = (isInsurer
            ? await fetchInsurerRequestHospitalActs(Number(hospitalId))
            : await fetchAdminRequestActs(Number(hospitalId), insurerId ? Number(insurerId) : undefined)) as {
            medical_acts?: Array<Record<string, unknown>>;
          };
          if (cancelled) return;
          setAvailableActs(
            (result.medical_acts ?? []).map((act) => {
              const id = act.id ? Number(act.id) : null;
              const code = String(act.code ?? "");
              return {
                key: actKey(id, code),
                id,
                code,
                label: String(act.label ?? ""),
                unitPrice: Number(act.cost ?? 0),
                source: String(act.source ?? ""),
              };
            }),
          );
        } else {
          const result = await fetchHospitalMedicalActs();
          if (cancelled) return;
          setAvailableActs(
            (result.medical_acts ?? []).map((act) => ({
              key: actKey(Number(act.id), String(act.code ?? "")),
              id: Number(act.id),
              code: String(act.code ?? ""),
              label: String(act.label || act.libelle || ""),
              unitPrice: actUnitPrice(act),
            })),
          );
        }
        // En édition, ne pas écraser les actes déjà chargés depuis la demande.
        if (!isEditMode) {
          setSelectedActs([]);
        }
      } catch {
        if (!cancelled) setAvailableActs([]);
      } finally {
        if (!cancelled) setLoadingActs(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canCreate, hospitalId, insurerId, isDesk, isEditMode, isInsurer]);

  const resetPatientSelection = () => {
    setPatientType("employee");
    setSelectedDependentId("");
    setFamilyMembers([]);
    setEmployeeLabel("");
  };

  // Charger les employés de l'entreprise sélectionnée (comme le wizard legacy).
  useEffect(() => {
    const cid = Number(companyId);
    if (!companyId || !Number.isFinite(cid) || cid <= 0 || !getAuthToken()) {
      setCompanyEmployees([]);
      return;
    }

    let cancelled = false;
    setLoadingEmployees(true);
    void (async () => {
      try {
        if (isDesk) {
          const result = isInsurer ? await fetchInsurerRequestEmployees(cid) : await fetchAdminEmployees({ company_id: cid });
          if (cancelled) return;
          setCompanyEmployees(
            (result.employees ?? []).map((row) => ({
              id: Number(row.id),
              name: String(row.name ?? ""),
              system_id: String(row.system_id ?? ""),
              company_id: Number(row.company_id ?? cid),
            })),
          );
        } else {
          const rows = await fetchHospitalCompanyEmployees(cid);
          if (cancelled) return;
          setCompanyEmployees(
            rows.map((row) => ({
              id: Number(row.id),
              name: String(row.name ?? ""),
              system_id: String(row.system_id ?? ""),
              company_id: Number(row.company_id ?? cid),
            })),
          );
        }
      } catch {
        if (!cancelled) setCompanyEmployees([]);
      } finally {
        if (!cancelled) setLoadingEmployees(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, isDesk]);

  useEffect(() => {
    const id = Number(employeeId);
    if (!employeeId || !Number.isFinite(id) || id <= 0) {
      setFamilyMembers([]);
      setSelectedDependentId("");
      return;
    }

    // Ne charger les ayants droit que si l'employé appartient à l'entreprise choisie.
    const cid = Number(companyId);
    if (companyId && Number.isFinite(cid) && cid > 0) {
      const inCompany = companyEmployees.some((row) => row.id === id);
      // Liste pas encore chargée : attendre ; si chargée et absent → vider.
      if (companyEmployees.length > 0 && !inCompany) {
        setFamilyMembers([]);
        setSelectedDependentId("");
        return;
      }
    }

    let cancelled = false;
    void (async () => {
      setLoadingDependents(true);
      try {
        const companyFilter = Number(companyId) > 0 ? Number(companyId) : undefined;
        const members = isDesk
          ? await (isInsurer ? fetchInsurerRequestFamily : fetchAdminEmployeeFamilyMembers)(id)
          : await fetchHospitalEmployeeFamilyMembers(id, companyFilter);
        if (cancelled) return;
        setFamilyMembers(
          members.map((member) => ({
            id: member.id,
            name: member.name,
            relationship: member.relationship,
          })),
        );
        setSelectedDependentId((current) =>
          current && members.some((member) => String(member.id) === current) ? current : "",
        );
      } catch {
        if (!cancelled) {
          setFamilyMembers([]);
          setSelectedDependentId("");
        }
      } finally {
        if (!cancelled) setLoadingDependents(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyEmployees, companyId, employeeId, isDesk]);

  useEffect(() => {
    if (patientType === "dependent" && !loadingDependents && familyMembers.length === 0) {
      setPatientType("employee");
      setSelectedDependentId("");
    }
  }, [familyMembers.length, loadingDependents, patientType]);

  useEffect(() => {
    if (!isEditMode || !editId || !getAuthToken()) return;

    let cancelled = false;
    setLoadingEdit(true);
    void (async () => {
      try {
        const row = await fetchAuditorProforma(editId);
        if (cancelled) return;
        if (row.status !== "pending") {
          showToast({ message: "Seules les demandes en attente peuvent être modifiées.", variant: "warning" });
          saveSuccess("/dashboard/requests", "");
          return;
        }

        setHospitalId(String(row.hospital_id ?? ""));
        setPackageId(String(row.package_id ?? ""));
        setInsurerId(row.insurer_id ? String(row.insurer_id) : "");
        setInsurancePackageId(row.insurance_package_id ? String(row.insurance_package_id) : "");
        setCompanyId(String(row.company_id ?? ""));
        setEmployeeId(String(row.employee_id ?? ""));
        setSystemId(row.employee_id_label || row.patient_id || "");
        setEmployeeLabel(
          row.employee_name
            ? `${row.employee_name} (${row.employee_id_label || row.patient_id})`
            : "",
        );
        setPatientType(row.patient_type === "dependent" ? "dependent" : "employee");
        setUrgency(String(row.urgency_raw ?? row.urgency ?? "normal"));
        setHospitalNotes(row.hospital_notes ?? "");
        setSelectedActs(
          (row.request_acts ?? [])
            .map((act) => ({
              key: actKey(Number(act.hospital_act_id || 0), String(act.code ?? "")),
              hospital_act_id: Number(act.hospital_act_id || 0),
              code: String(act.code ?? ""),
              label: String(act.label ?? ""),
              quantity: Math.max(1, Number(act.quantity) || 1),
              unit_price: Number(act.unit_price) || 0,
            }))
            .filter((act) => act.hospital_act_id > 0),
        );

        const members = await (isInsurer ? fetchInsurerRequestFamily : fetchAdminEmployeeFamilyMembers)(Number(row.employee_id));
        if (cancelled) return;
        setFamilyMembers(
          members.map((member) => ({
            id: member.id,
            name: member.name,
            relationship: member.relationship,
          })),
        );
        if (row.patient_type === "dependent" && row.dependent_name) {
          const match = members.find((member) => member.name === row.dependent_name);
          if (match) setSelectedDependentId(String(match.id));
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Impossible de charger la demande.";
          showToast({ message, variant: "warning" });
          saveSuccess("/dashboard/requests", "");
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editId, isEditMode, saveSuccess, showToast]);

  const handleLookup = async () => {
    if (!systemId.trim()) {
      showToast({ message: formLabels.systemIdRequired, variant: "warning" });
      return;
    }
    if (!getAuthToken()) {
      showToast({ message: "Lookup disponible avec une session API.", variant: "warning" });
      return;
    }

    setLookingUp(true);
    try {
      const companyFilter = Number(companyId) > 0 ? Number(companyId) : undefined;
      if (isDesk) {
        const result = await (isInsurer ? lookupInsurerRequestEmployee : lookupAdminEmployee)({
          matricule: systemId.trim(),
          company_id: companyFilter,
        });
        setEmployeeId(String(result.employee.id));
        setCompanyId(String(result.employee.company_id));
        setSystemId(result.employee.matricule || systemId.trim());
        setEmployeeLabel(
          `${result.employee.employee_name} (${result.employee.matricule}) — ${result.employee.company_name}`,
        );
        setFamilyMembers(
          (result.family_members ?? []).map((member) => ({
            id: member.id,
            name: member.name,
            relationship: member.relationship,
          })),
        );
        setPatientType("employee");
        setSelectedDependentId("");
      } else {
        const result = await lookupHospitalEmployeeByMatricule(systemId.trim(), companyFilter);
        setEmployeeId(String(result.employee.id));
        setCompanyId(String(result.employee.company_id));
        setSystemId(result.employee.matricule || systemId.trim());
        setEmployeeLabel(
          `${result.employee.employee_name} (${result.employee.matricule}) — ${result.employee.company_name}`,
        );
        setFamilyMembers(
          (result.family_members ?? []).map((member) => ({
            id: member.id,
            name: member.name,
            relationship: member.relationship,
          })),
        );
        setPatientType("employee");
        setSelectedDependentId("");
      }
      showToast({ message: "Assuré trouvé.", variant: "success" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Assuré introuvable.";
      showToast({ message, variant: "warning" });
      setEmployeeId("");
      resetPatientSelection();
    } finally {
      setLookingUp(false);
    }
  };

  const handleSelectEmployee = (rawId: string) => {
    setEmployeeId(rawId);
    setPatientType("employee");
    setSelectedDependentId("");
    const selected = companyEmployees.find((row) => String(row.id) === rawId);
    if (selected) {
      setSystemId(selected.system_id);
      setEmployeeLabel(
        `${selected.name}${selected.system_id ? ` (${selected.system_id})` : ""}`,
      );
    } else {
      setEmployeeLabel("");
      setFamilyMembers([]);
    }
  };

  const handleAddAct = (key: string) => {
    const act = availableActs.find((item) => item.key === key);
    if (!act) {
      showToast({
        message: "Acte introuvable dans la grille. Affinez la recherche ou rechargez la page.",
        variant: "warning",
      });
      return;
    }

    if (selectedActs.some((row) => row.key === key)) {
      showToast({ message: "Cet acte est déjà dans la liste.", variant: "warning" });
      return;
    }

    setSelectedActs((rows) => [
      ...rows,
      {
        key: act.key,
        hospital_act_id: act.id ?? 0,
        code: act.code,
        label: act.label,
        quantity: 1,
        unit_price: Number.isFinite(act.unitPrice) ? act.unitPrice : 0,
      },
    ]);
    setActSearch("");
  };

  const handleRemoveAct = (key: string) => {
    setSelectedActs((rows) => rows.filter((row) => row.key !== key));
  };

  const handleUpdateActQuantity = (key: string, quantity: number) => {
    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    setSelectedActs((rows) => rows.map((row) => (row.key === key ? { ...row, quantity: safeQty } : row)));
  };

  /** Acte saisi à la main (absent de la grille et du catalogue) : ajouté au catalogue du prestataire à l'enregistrement. */
  const handleAddManualAct = () => {
    const label = manualAct.label.trim();
    const price = Number(manualAct.price.replace(",", "."));
    if (!label || !Number.isFinite(price) || price <= 0) {
      showToast({ message: "Indiquez le libellé et le prix de l'acte.", variant: "warning" });
      return;
    }
    const code = manualAct.code.trim().toUpperCase();
    setSelectedActs((rows) => [
      ...rows,
      { key: `m${Date.now()}`, hospital_act_id: 0, code, label, quantity: 1, unit_price: price },
    ]);
    setManualAct({ code: "", label: "", price: "" });
  };

  const handleSubmit = async () => {
    if ((isDesk && (!insurerId || !hospitalId)) || (packageRequired && !insurancePackageId) || !companyId || !employeeId) {
      showToast({
        message: "Assureur, package, hôpital, entreprise et patient sont obligatoires.",
        variant: "warning",
      });
      return;
    }

    const selectedDependent = familyMembers.find(
      (member) => String(member.id) === selectedDependentId,
    );

    if (patientType === "dependent" && !selectedDependent) {
      showToast({ message: formLabels.dependentRequired, variant: "warning" });
      return;
    }

    if (selectedActs.length === 0) {
      showToast({ message: formLabels.actsRequired, variant: "warning" });
      return;
    }

    if (!getAuthToken() || !canCreate) {
      saveSuccess("/dashboard/requests", t.actions.saved);
      return;
    }

    setSaving(true);
    try {
      const acts: CreateProformaActPayload[] = selectedActs.map((row) =>
        row.hospital_act_id > 0
          ? { hospital_act_id: row.hospital_act_id, quantity: row.quantity, unit_price: row.unit_price }
          : { code: row.code || undefined, label: row.label, quantity: row.quantity, unit_price: row.unit_price },
      );

      const payload = {
        package_id: packageId ? Number(packageId) : undefined,
        insurance_package_id: insurancePackageId ? Number(insurancePackageId) : undefined,
        company_id: Number(companyId),
        employee_id: Number(employeeId),
        estimated_amount: estimatedTotal,
        urgency,
        hospital_notes: hospitalNotes.trim() || undefined,
        patient_type: patientType,
        dependent_name:
          patientType === "dependent" ? selectedDependent?.name : undefined,
        acts,
        proforma_pdf: proformaPdf ?? undefined,
      };

      if (isEditMode && editId) {
        await updateAdminProforma(editId, { ...payload, hospital_id: Number(hospitalId) });
      } else if (isDesk) {
        await createAdminProforma({ ...payload, hospital_id: Number(hospitalId) });
      } else {
        await createHospitalProforma(payload);
      }
      saveSuccess("/dashboard/requests", t.actions.saved);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : isEditMode
              ? "Mise à jour de la demande impossible."
              : "Création de la demande impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSaving(false);
    }
  };

  if (!canCreate && !isEditMode) {
    return (
      <>
        <Breadcrumbs
          homeHref={homeHref}
          items={[
            { label: labels.title, href: "/dashboard/requests" },
            { label: labels.add },
          ]}
        />
        <div className="theme-card p-5">
          <p className="text-sm text-muted">
            Seuls les comptes Assurance et Admin peuvent saisir une demande de validation.
          </p>
          <Link to="/dashboard/requests" className="mt-4 inline-flex text-sm text-primary">
            Retour à la liste
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref={homeHref}
          items={[
            { label: labels.title, href: "/dashboard/requests" },
            { label: labels.add },
          ]}
        />
        <Link
          to="/dashboard/requests"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
        >
          <ArrowLeft size={16} />
          Retour à la liste
        </Link>
      </div>

      <div className="theme-card p-5 md:p-6">
        <div className="mb-6 flex items-center gap-2 text-base font-semibold text-foreground">
          <Info size={16} className="text-primary" />
          {isEditMode ? `Modifier la demande #${editId}` : "Nouvelle demande d'autorisation"}
        </div>

        {loadingEdit && (
          <p className="mb-4 text-sm text-muted-foreground">Chargement de la demande…</p>
        )}

        {isEditMode && !loadingEdit && (
          <div className="mb-5 rounded-xl border border-primary/20 bg-primary-soft px-4 py-3 text-sm text-foreground">
            Modification d&apos;une demande en attente (#{editId}).
          </div>
        )}

        {isDesk && !isEditMode && (
          <div className="mb-5 rounded-xl border border-primary/20 bg-primary-soft px-4 py-3 text-sm text-foreground">
            {isInsurer
              ? "Saisissez la demande de validation envoyée par l'hôpital. Elle sera validée ou rejetée par l'Audit."
              : "Remplissez ce formulaire à partir de la demande reçue par email de l'hôpital."}
          </div>
        )}

        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          {isDesk && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField label="Assureur" required>
                <select
                  className={selectClass}
                  value={insurerId}
                  disabled={isInsurer && insurers.length === 1}
                  onChange={(event) => {
                    setInsurerId(event.target.value);
                    setInsurancePackageId("");
                    setHospitalId("");
                    setCompanyId("");
                    setEmployeeId("");
                    resetPatientSelection();
                  }}
                  required
                >
                  <option value="">— Sélectionner l&apos;assureur —</option>
                  {insurers.map((insurer) => (
                    <option key={insurer.id} value={insurer.id}>
                      {insurer.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Package" required={packageRequired} hint={insurerId && !packageRequired ? "Aucun package actif pour cet assureur." : undefined}>
                <select
                  className={selectClass}
                  value={insurancePackageId}
                  disabled={!insurerId || insurerPackages.length === 0}
                  onChange={(event) => {
                    const next = insurancePackages.find((pkg) => pkg.id === Number(event.target.value));
                    setInsurancePackageId(event.target.value);
                    if (next && next.hospital_ids.length > 0 && !next.hospital_ids.includes(Number(hospitalId))) setHospitalId("");
                    if (next && next.company_ids.length > 0 && !next.company_ids.includes(Number(companyId))) {
                      setCompanyId("");
                      setEmployeeId("");
                      resetPatientSelection();
                    }
                  }}
                  required={packageRequired}
                >
                  <option value="">{insurerId ? "— Sélectionner le package —" : "Choisissez d'abord l'assureur"}</option>
                  {insurerPackages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.code} — {pkg.name}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          )}

          {isDesk && (
            <FormField label="Hôpital" required>
              <select
                className={selectClass}
                value={hospitalId}
                onChange={(event) => setHospitalId(event.target.value)}
                disabled={!insurerId || (packageRequired && !insurancePackageId)}
                required
              >
                <option value="">— Sélectionner l&apos;hôpital —</option>
                {visibleHospitals.map((hospital) => (
                  <option key={hospital.id} value={hospital.id}>
                    {hospital.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          {!isDesk && (
          <FormField label="Package" required>
            <select
              className={selectClass}
              value={packageId}
              onChange={(event) => setPackageId(event.target.value)}
              required
              disabled={isDesk && !hospitalId}
            >
              <option value="">
                {isDesk && !hospitalId
                  ? "Sélectionnez d'abord l'hôpital"
                  : "— Sélectionner un package —"}
              </option>
              {filteredPackages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.code} — {pkg.name}
                </option>
              ))}
            </select>
          </FormField>
          )}

          <div className="rounded-xl border border-border bg-card-muted/30 p-4">
            <p className="mb-3 text-sm font-medium text-foreground">
              {formLabels.systemIdSearch}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                className={inputClass}
                value={systemId}
                onChange={(event) => setSystemId(event.target.value)}
                placeholder={formLabels.systemIdPlaceholder}
                disabled={lookingUp || saving}
              />
              <button
                type="button"
                onClick={() => void handleLookup()}
                disabled={lookingUp || saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
              >
                <Search size={16} />
                {lookingUp ? "Recherche…" : "Rechercher"}
              </button>
            </div>
            {employeeLabel && (
              <p className="mt-2 text-sm text-accent-foreground">{employeeLabel}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Entreprise" required>
              <select
                className={selectClass}
                value={companyId}
                onChange={(event) => {
                  setCompanyId(event.target.value);
                  setEmployeeId("");
                  setSystemId("");
                  resetPatientSelection();
                }}
                required
              >
                <option value="">— Sélectionner —</option>
                {visibleCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Assuré" required>
              <select
                className={selectClass}
                value={employeeId}
                onChange={(event) => handleSelectEmployee(event.target.value)}
                required
                disabled={!companyId || loadingEmployees}
              >
                <option value="">
                  {!companyId
                    ? "Sélectionnez d'abord l'entreprise"
                    : loadingEmployees
                      ? "Chargement des assurés…"
                      : "— Sélectionner un assuré —"}
                </option>
                {companyEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                    {employee.system_id ? ` (${employee.system_id})` : ""}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <div className="rounded-xl border border-border bg-card-muted/30 p-4 space-y-4">
            <FormField label={formLabels.patientType}>
              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="patient_type"
                    value="employee"
                    checked={patientType === "employee"}
                    onChange={() => {
                      setPatientType("employee");
                      setSelectedDependentId("");
                    }}
                  />
                  {formLabels.patientEmployee}
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="patient_type"
                    value="dependent"
                    checked={patientType === "dependent"}
                    disabled={loadingDependents || familyMembers.length === 0}
                    onChange={() => setPatientType("dependent")}
                  />
                  {formLabels.patientDependent}
                </label>
              </div>
            </FormField>

            {patientType === "dependent" && (
              <FormField label={formLabels.dependentName} required>
                {loadingDependents ? (
                  <p className="text-sm text-muted-foreground">{formLabels.loadingDependents}</p>
                ) : familyMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{formLabels.noDependents}</p>
                ) : (
                  <select
                    className={selectClass}
                    value={selectedDependentId}
                    onChange={(event) => setSelectedDependentId(event.target.value)}
                    required
                  >
                    <option value="">{formLabels.dependentPlaceholder}</option>
                    {familyMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                        {member.relationship ? ` (${member.relationship})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card-muted/30 p-4 space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground">{formLabels.actsTitle}</p>
              <p className="mt-1 text-xs text-muted-foreground">{formLabels.actsHint}</p>
            </div>

            {isDesk && !hospitalId ? (
              <p className="text-sm text-muted-foreground">{formLabels.selectHospitalFirst}</p>
            ) : loadingActs ? (
              <p className="text-sm text-muted-foreground">Chargement des actes…</p>
            ) : (
              <>
                <div className="space-y-3">
                  <input
                    className={inputClass}
                    value={actSearch}
                    onChange={(event) => setActSearch(event.target.value)}
                    placeholder={formLabels.actSearch}
                  />
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-card">
                    {filteredAvailableActs.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-muted-foreground">
                        {availableActs.length === 0
                          ? "Aucun acte dans la grille ni le catalogue de ce prestataire : ajoutez l'acte ci-dessous."
                          : "Aucun acte correspondant — affinez la recherche ou ajoutez l'acte ci-dessous."}
                      </p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {filteredAvailableActs.map((act) => (
                          <li key={act.key}>
                            <button
                              type="button"
                              onClick={() => handleAddAct(act.key)}
                              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent/20"
                            >
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-card-muted text-muted-foreground">
                                <Plus size={14} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-medium text-foreground">
                                  {hideActCodes ? null : act.code || "—"}
                                  {act.source === "grid" ? (
                                    <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-normal text-primary">grille</span>
                                  ) : null}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {act.label || "Sans libellé"}
                                </span>
                              </span>
                              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                {(Number.isFinite(act.unitPrice) ? act.unitPrice : 0).toFixed(2)} USD
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {filteredAvailableActs.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {filteredAvailableActs.length} acte(s) — cliquez pour ajouter à la liste.
                    </p>
                  )}
                  <div className="grid grid-cols-1 gap-2 rounded-lg border border-dashed border-border p-3 md:grid-cols-12">
                    <p className="text-xs font-medium text-foreground md:col-span-12">Ajouter un acte qui n'est pas dans la liste</p>
                    {hideActCodes ? null : (
                      <input
                        className={`${inputClass} md:col-span-3`}
                        value={manualAct.code}
                        onChange={(event) => setManualAct({ ...manualAct, code: event.target.value })}
                        placeholder="Code (facultatif)"
                        aria-label="Code de l'acte"
                      />
                    )}
                    <input
                      className={`${inputClass} md:col-span-5`}
                      value={manualAct.label}
                      onChange={(event) => setManualAct({ ...manualAct, label: event.target.value })}
                      placeholder="Libellé de l'acte"
                      aria-label="Libellé de l'acte"
                    />
                    <input
                      className={`${inputClass} md:col-span-2`}
                      value={manualAct.price}
                      onChange={(event) => setManualAct({ ...manualAct, price: event.target.value })}
                      placeholder="Prix (USD)"
                      inputMode="decimal"
                      aria-label="Prix de l'acte"
                    />
                    <button
                      type="button"
                      onClick={handleAddManualAct}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-accent/20 hover:border-accent md:col-span-2"
                    >
                      <Plus size={14} />
                      Ajouter
                    </button>
                  </div>
                </div>

                {selectedActs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{formLabels.noActs}</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="min-w-full text-sm">
                      <thead className="bg-card-muted/50 text-left text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2">Acte</th>
                          <th className="px-3 py-2">{formLabels.quantity}</th>
                          <th className="px-3 py-2">{formLabels.unitPrice}</th>
                          <th className="px-3 py-2">{formLabels.lineTotal}</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {selectedActs.map((row) => (
                          <tr key={row.key} className="border-t border-border">
                            <td className="px-3 py-2">
                              {hideActCodes ? null : <div className="font-medium">{row.code}</div>}
                              <div className="text-xs text-muted-foreground">{row.label}</div>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                className={`${inputClass} w-20`}
                                type="number"
                                min={1}
                                step={1}
                                value={row.quantity}
                                onChange={(event) =>
                                  handleUpdateActQuantity(
                                    row.key,
                                    Number(event.target.value),
                                  )
                                }
                              />
                            </td>
                            <td className="px-3 py-2">{row.unit_price.toFixed(2)}</td>
                            <td className="px-3 py-2">
                              {(row.quantity * row.unit_price).toFixed(2)}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => handleRemoveAct(row.key)}
                                className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-card-muted hover:text-destructive"
                                aria-label="Retirer l'acte"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label={formLabels.estimatedAmount} required>
              <input
                className={`${inputClass} bg-card-muted/50`}
                type="text"
                readOnly
                value={estimatedTotal.toFixed(2)}
              />
              <p className="mt-1 text-xs text-muted-foreground">{formLabels.estimatedHint}</p>
            </FormField>

            <FormField label={formLabels.urgencyLevel}>
              <select
                className={selectClass}
                value={urgency}
                onChange={(event) => setUrgency(event.target.value)}
              >
                <option value="normal">Normal — traitement sous 1 h</option>
                <option value="urgent">Urgent — traitement sous 20 min</option>
              </select>
            </FormField>
          </div>

          <FormField label="Notes hôpital">
            <textarea
              className={textareaClass}
              rows={3}
              value={hospitalNotes}
              onChange={(event) => setHospitalNotes(event.target.value)}
              placeholder="Contexte clinique, précisions sur la demande…"
            />
          </FormField>

          <FormField label="PDF proforma">
            <input
              className={inputClass}
              type="file"
              accept="application/pdf"
              onChange={(event) => setProformaPdf(event.target.files?.[0] ?? null)}
            />
          </FormField>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              <Save size={16} />
              {saving ? "Enregistrement…" : isEditMode ? "Enregistrer les modifications" : "Créer la demande"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
